import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import {
  WaitingCall,
  useCallInProgressQuery,
  useGetRelationshipToDealByPhoneNumberQuery,
  useGetUserTwilioNumberQuery,
  useGetWaitingCallsQuery,
  useInProgressCallsSubscription,
  useOnWaitingQueueSubscription,
} from '../gql/generated/graphql';

import TransferCallModal from '../components/Calls/transferCallModal';
import { useUser } from '../hooks/useUser';

export type Call =
  | {
      fullname: string;
      phoneNumber: string | undefined;
      toNumber: string | undefined;
      conferenceId: string;
      dealId: number;
      dealType?: string;
      isThunder: boolean;
      thunderData: {
        thunder_call_notification_id: number;
        scheduled_call_time?: string;
        attempt_num: number;
        ai_call_opener?: string;
        ai_call_opener_variant?: string;
        thunder_phone_number?: string;
        // Inbound-callback fields populated by leaseend-api's createWaitingCall when an
        // inbound call lands on a Thunder receive number (LE-16538).
        is_callback?: boolean;
        last_attempt_timestamp?: string;
      } | null;
      callId: string;
      dealState: string;
      started_at: string;
      huntGroupSlug?: string;
      is_outbound?: boolean;
      relationship?: string;
      id: number | null;
    }
  | undefined;

type CallsContextType = {
  refresh: () => void;
  isRefreshing?: boolean;
  current: Call | undefined;
  setCurrent: (value: Call | undefined) => void;
  waitingCalls: Call[];
  activeCall: Call | null;
  twilioNumber: string | undefined;
  handleTransferCallModal: (isVisible: boolean) => void;
  isVisibleTransferCallModal: boolean;
  callAlreadyAnswered: boolean;
  setCallAlreadyAnswered: (v: boolean) => void;
  answeredAt: Date | null;
  sipFailureOccurred: boolean;
  setSipFailureOccurred: (v: boolean) => void;
};

export const CallsContext = createContext<CallsContextType>({
  refresh: () => false,
  handleTransferCallModal: () => false,
  current: undefined,
  setCurrent: () => undefined,
  isVisibleTransferCallModal: false,
  isRefreshing: false,
  waitingCalls: [],
  activeCall: undefined,
  twilioNumber: undefined,
  callAlreadyAnswered: false,
  setCallAlreadyAnswered: () => undefined,
  answeredAt: null,
  sipFailureOccurred: false,
  setSipFailureOccurred: () => undefined,
});

type WaitingCallObjectType = {
  [key: string]: WaitingCall[];
};

export const CallsProvider = ({ children }: { children: React.ReactNode }) => {
  // `current` is what is being presented to dd user
  // it could be a waiting call or an active call
  const [current, setCurrent] = useState<Call>(undefined);
  const [waitingCallsObject, setWaitingCallsObject] = useState<WaitingCallObjectType>({});
  const [waitingCalls, setWaitingCalls] = useState<Call[]>([]);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [answeredAt, setAnsweredAt] = useState<Date | null>(null);
  const [callAlreadyAnswered, setCallAlreadyAnswered] = useState(false);
  const [sipFailureOccurred, setSipFailureOccurred] = useState(false);
  const [isVisibleTransferCallModal, setIsVisibleTransferCallModal] = useState(false);

  const user = useUser();

  // waiting queue
  const { data: getWaitingQueueQueryData, refetch: refetchWaitingCalls } = useGetWaitingCallsQuery({
    skip: !user?.id,
  });
  const { data: waitingQueueSubscriptionData, loading } = useOnWaitingQueueSubscription();

  // in progress calls
  const { data: inProgressCallsQueryData } = useCallInProgressQuery({
    skip: !user?.id,
  });
  const { data: inProgressCallsSubscriptionData } = useInProgressCallsSubscription();

  const userTwilioNumber = useGetUserTwilioNumberQuery({
    skip: !user?.id,
  });

  // Get customer data when there's an active call
  const { data: relationshipData } = useGetRelationshipToDealByPhoneNumberQuery({
    skip: !activeCall?.phoneNumber,
    variables: {
      phoneNumber: activeCall?.phoneNumber ?? '',
    },
    onCompleted: (data) => {
      if (!activeCall) {
        return;
      }
      setActiveCall({
        ...activeCall,
        relationship: data?.getRelationshipToDealByPhoneNumber?.relationship ?? '',
      });
    },
  });

  // in progress calls logic
  useEffect(() => {
    if (user && user.call_status === 'inactive') {
      return;
    }

    const inProgressCallsFromSubscription = inProgressCallsSubscriptionData?.inProgressCalls;
    const inProgressCallFromQuery = inProgressCallsQueryData?.callInProgress;

    const inProgressCalls = inProgressCallsFromSubscription?.length
      ? inProgressCallsFromSubscription
      : [inProgressCallFromQuery];

    const currentInProgressCall = inProgressCalls?.find((item) => item?.agent_user_id === user?.id);

    if (currentInProgressCall && !activeCall) {
      const fromSplit = currentInProgressCall?.from?.split('-') ?? [];
      setActiveCall({
        fullname: currentInProgressCall.customer_name ?? '',
        dealId: currentInProgressCall.deal_id as number,
        dealType: currentInProgressCall.deal_type ?? '',
        isThunder: currentInProgressCall.is_thunder ?? false,
        phoneNumber: fromSplit[0],
        toNumber: currentInProgressCall.to ?? '',
        conferenceId: currentInProgressCall?.conference_id ?? '',
        callId: fromSplit[1],
        dealState: currentInProgressCall?.deal_state ?? '',
        started_at: waitingCalls?.[0]?.started_at ?? '',
        huntGroupSlug: currentInProgressCall?.hunt_group_slug ?? '',
        is_outbound: !!currentInProgressCall?.is_outbound,
        relationship: relationshipData?.getRelationshipToDealByPhoneNumber?.relationship ?? '',
        thunderData: null,
        id: currentInProgressCall?.id ?? null,
      });
    } else if (!currentInProgressCall && activeCall) {
      setActiveCall(null);
    }
  }, [inProgressCallsSubscriptionData?.inProgressCalls, inProgressCallsQueryData?.callInProgress]);

  // When a SIP failure occurs, clear activeCall directly.
  // The normal IN_PROGRESS_CALLS subscription path won't clear it because
  // the in-progress calls useEffect returns early when user is inactive.
  useEffect(() => {
    if (sipFailureOccurred) {
      setActiveCall(null);
    }
  }, [sipFailureOccurred]);

  // logic for waiting calls from query
  useEffect(() => {
    const { data: waitingQueuesObjectFromQuery = null } =
      getWaitingQueueQueryData?.getWaitingCalls ?? {};

    if (waitingQueuesObjectFromQuery) {
      setWaitingCallsObject(waitingQueuesObjectFromQuery as WaitingCallObjectType);
    }
  }, [getWaitingQueueQueryData?.getWaitingCalls]);

  // logic for waiting calls from subscription
  useEffect(() => {
    const { data: waitingQueueFromSubscription = null, hunt_group_slug = '' } =
      waitingQueueSubscriptionData?.onWaitingQueue ?? {};

    // if a waiting queue has come through a subscription
    // replace the value in the waitingCallsObject
    if (waitingQueueFromSubscription && hunt_group_slug) {
      setWaitingCallsObject({
        ...waitingCallsObject,
        [hunt_group_slug]: waitingQueueFromSubscription as WaitingCall[],
      });
    }
  }, [waitingQueueSubscriptionData?.onWaitingQueue]);

  // waiting calls object
  useEffect(() => {
    // merge waiting calls from object based on time
    const waitingQueue = Object.values(waitingCallsObject)
      .flat()
      .sort(
        (a, b) =>
          new Date(a.started_at as string).getTime() - new Date(b.started_at as string).getTime(),
      );

    const waitingQueueCleaned = (waitingQueue as WaitingCall[])?.map((item: WaitingCall): Call => {
      // FROM +18018983841-CA4914195f6e4b74245c24c0b06595914d-+18585001062-CFc7191b3ce10fb2952e149f6aa6f06072
      const fromSplit = item?.from?.split('-') ?? [];
      return {
        fullname: item.customer_name ?? '',
        dealId: item.deal_id as number,
        dealType: item.deal_type ?? '',
        isThunder: item.is_thunder ?? false,
        phoneNumber: fromSplit[0],
        toNumber: fromSplit[2],
        conferenceId: item?.conference_id ?? '',
        callId: fromSplit[1],
        dealState: item?.deal_state ?? '',
        started_at: item?.started_at ?? '',
        huntGroupSlug: item?.hunt_group_slug ?? '',
        thunderData: item?.thunder_data ?? null,
        id: item?.id ?? null,
      };
    });

    setWaitingCalls(waitingQueueCleaned);
  }, [waitingCallsObject]);

  // logic to set `current`
  useEffect(() => {
    if (user?.call_status === 'inactive') {
      // if user is not available, set current to undefined
      setCurrent(undefined);
    } else if (activeCall) {
      // if there is an active call, set current to active call
      setCurrent(activeCall);
    } else if (!activeCall && waitingCalls?.length) {
      // if there is no active call, but there are waiting calls, set current to first waiting call
      setCurrent(waitingCalls[0]);
    } else if (!activeCall && !waitingCalls?.length) {
      // if there is no active call and no waiting calls, set current to undefined
      setCurrent(undefined);
    }
  }, [waitingCalls, activeCall, user?.call_status]);

  useEffect(() => {
    if (activeCall && !answeredAt) {
      setAnsweredAt(new Date());
    } else if (!activeCall && answeredAt) {
      setAnsweredAt(null);
    }
  }, [activeCall]);

  const handleTransferCallModal = useCallback((isVisible: boolean) => {
    setIsVisibleTransferCallModal(isVisible);
  }, []);

  const refresh = useCallback(() => {
    refetchWaitingCalls().then((result) => {
      const newData = result.data?.getWaitingCalls?.data;
      if (newData) {
        setWaitingCallsObject(newData as WaitingCallObjectType);
      } else {
        setWaitingCallsObject({});
      }
    });
  }, [refetchWaitingCalls]);

  return (
    <CallsContext.Provider
      value={{
        refresh,
        activeCall,
        current,
        setCurrent,
        isRefreshing: loading,
        waitingCalls,
        handleTransferCallModal,
        isVisibleTransferCallModal,
        twilioNumber: userTwilioNumber.data?.getUserTwilioNumber ?? undefined,
        callAlreadyAnswered,
        setCallAlreadyAnswered,
        answeredAt,
        sipFailureOccurred,
        setSipFailureOccurred,
      }}
    >
      {children}
      {isVisibleTransferCallModal ? <TransferCallModal /> : null}
    </CallsContext.Provider>
  );
};

export const useCallsContext = () => useContext(CallsContext);
